/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * --- REWRITE SUMMARY (V45 - Search Navigation Fix: Part 2) ---
 * - MISSION: To upgrade the LazyLoad component so it can be programmatically
 * forced to render its children, even if they are not yet in the viewport.
 *
 * - THE FIX (Conditional Rendering Logic):
 * 1.  NEW PROP: The component now accepts a `forceVisible` boolean prop.
 * 2.  UPDATED LOGIC: The main conditional check has been modified. The
 * component will now render its `children` if `isVisible` (the state
 * controlled by the IntersectionObserver) is true, OR if the new
 * `forceVisible` prop is true.
 * 3.  OPTIMIZATION: The `useEffect` hook that sets up the
 * IntersectionObserver is now disabled if `forceVisible` is true from the
 * start, preventing unnecessary work.
 *
 * - RESULT: This is a critical upgrade. It gives the parent component
 * (`Home.jsx`) the power to override the default lazy-loading behavior on
 * demand. When a search result is clicked, `Home.jsx` will pass
 * `forceVisible={true}` to all preceding sections, ensuring their DOM
 * elements are rendered immediately so that the browser can accurately
 * calculate the scroll position.
 */
import { useState, useRef, useEffect } from 'react';

const LazyLoad = ({ children, minHeight = '100vh', forceVisible = false }) => {
  const [isVisible, setIsVisible] = useState(forceVisible);
  const placeholderRef = useRef(null);

  useEffect(() => {
    // If forced visible from the start, no need to observe.
    if (forceVisible) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: '50px 0px',
        threshold: 0.01,
      }
    );

    const currentRef = placeholderRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [forceVisible]);

  // --- MODIFIED: Render if naturally visible OR if forced ---
  return (
    <>
      {isVisible || forceVisible ? (
        children
      ) : (
        <div
          ref={placeholderRef}
          style={{ minHeight }}
          aria-hidden="true"
        />
      )}
    </>
  );
};

export default LazyLoad;
